import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Clock, Zap, ShieldCheck, MessageSquare, Package, Bell, Flag, ChevronDown, Users, AlertTriangle } from 'lucide-react';
import { CommanServices } from '../services/commanServices';
import { dispatchTimelineFocus } from './timelineFocusController';

const SECTION_META = {
  response_action: { label: 'Response Action', icon: Zap, color: 'text-rose-600', bg: 'bg-rose-50', dot: 'bg-rose-500' },
  acknowledgment: { label: 'Verification', icon: ShieldCheck, color: 'text-emerald-600', bg: 'bg-emerald-50', dot: 'bg-emerald-500' },
  status_update: { label: 'Status Update', icon: MessageSquare, color: 'text-sky-600', bg: 'bg-sky-50', dot: 'bg-sky-500' },
  resource: { label: 'Resource', icon: Package, color: 'text-indigo-600', bg: 'bg-indigo-50', dot: 'bg-indigo-500' },
  notification: { label: 'Notification', icon: Bell, color: 'text-amber-600', bg: 'bg-amber-50', dot: 'bg-amber-500' },
  resolution: { label: 'Resolution', icon: Flag, color: 'text-gray-600', bg: 'bg-gray-50', dot: 'bg-gray-500' },
};
const DEFAULT_META = { label: 'Inject', icon: Zap, color: 'text-gray-600', bg: 'bg-gray-50', dot: 'bg-gray-400' };

// Every card except "Report & Detection" is an injection.
const isInjectGroup = (g) => !!g && g !== 'report';

const getOffset = (it) => {
  const v = it?.meta?.fireOffsetMinutes;
  return Number.isFinite(Number(v)) ? Number(v) : 0;
};

const getTitle = (it) => {
  if (it?.group === 'resource') return it?.meta?.resource?.category || it?.title || 'Resource';
  return it?.meta?.actionCardName || it?.title || (SECTION_META[it.group]?.label || 'Inject');
};

const getAssignee = (it) => {
  // Scenario planning is role-based — show the required role(s) first.
  const roles = it?.meta?.scenarioRoles;
  if (Array.isArray(roles) && roles.length) {
    return roles
      .filter((r) => r?.role)
      .map((r) => `${r.count > 1 ? `${r.count}× ` : ''}${r.role}`)
      .join(', ') || 'Role TBD';
  }
  if (it?.group === 'resource') {
    const r = it?.meta?.resource || {};
    const qty = r.quantity != null ? `${r.quantity}× ` : '';
    return `${qty}${r.assigneeName || 'Unassigned'}`;
  }
  const m = it?.meta || {};
  const a = m.acknowledgedBy;
  if (a?.userName) return a.userName;
  if (it?.actor?.name && it.actor.name !== 'Scenario' && it.actor.name !== 'System') return it.actor.name;
  if (Array.isArray(m.assignedUsers) && m.assignedUsers.length) return `${m.assignedUsers.length} assignees`;
  return 'Mixed Targets';
};

const getSla = (it) => it?.meta?.sla?.withinMinutes;

export const selectTimelinePreviewItems = (list = [], incident = {}) => (Array.isArray(list) ? list : [])
  .filter((it) => isInjectGroup(it.group) && it.is_active !== false && !it?.meta?.superseded)
  // Real incidents: this preview shows ONLY the scenario-pulled plan —
  // manual/live cards (e.g. admin notifications) never appear here.
  .filter((it) => incident?.is_exercise || incident?.is_scenario || !!it?.plan_state)
  // Exclude system audit entries (e.g. status-change logged on activation) — only
  // user-authored injects belong in the sequence.
  .filter((it) => !(it?.is_system_generated && (it?.action === 'STATUS_CHANGED' || /status changed from/i.test(it?.details || ''))))
  .sort((a, b) => getOffset(a) - getOffset(b));

export const buildEffectivePlanOffsetMap = (items = []) => {
  const byId = new Map((items || []).map((item) => [item?.id, item]));
  const cache = new Map();
  const calculate = (item) => {
    if (!item) return 0;
    if (cache.has(item.id)) return cache.get(item.id);
    const storedOffset = getOffset(item);
    cache.set(item.id, storedOffset); // cycle guard
    const conditional = item?.meta?.conditional;
    let effectiveOffset = storedOffset;
    if (conditional) {
      const source = byId.get(conditional.sourceItemId);
      const taskIndex = conditional.sourceTaskIndex;
      const task = taskIndex !== undefined && taskIndex !== null && taskIndex !== ''
        ? (source?.meta?.tasks || [])[Number(taskIndex)]
        : null;
      const taskSla = typeof task?.sla === 'object' ? task?.sla?.withinMinutes : task?.sla;
      const windowMinutes = taskSla !== undefined && taskSla !== null && taskSla !== '' && Number.isFinite(Number(taskSla))
        ? Number(taskSla)
        : (Number(source?.meta?.sla?.withinMinutes) || 0);
      const conditionOffset = (source ? calculate(source) : 0) + (Number.isFinite(windowMinutes) ? windowMinutes : 0);
      effectiveOffset = Math.max(storedOffset, conditionOffset);
    }
    cache.set(item.id, effectiveOffset);
    return effectiveOffset;
  };
  const result = {};
  (items || []).forEach((item) => {
    if (item?.id) result[item.id] = calculate(item);
  });
  return result;
};

// Only include plan cards that require a responder outcome. Status updates and
// resources are informational in the execution sequence and cannot demonstrate
// that an assignee performed work.
const PLAN_VERIFICATION_GROUPS = new Set(['acknowledgment', 'verification']);
const PLAN_ACTION_GROUPS = new Set(['response_action', 'action']);
const PLAN_EXECUTION_GROUPS = new Set([
  'notification',
  ...PLAN_VERIFICATION_GROUPS,
  ...PLAN_ACTION_GROUPS,
]);
const toValidDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const latestValidDate = (values = []) => values
  .map(toValidDate)
  .filter(Boolean)
  .sort((a, b) => b.getTime() - a.getTime())[0] || null;

const normalizedStatus = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ');

const taskIsRequired = (task) => {
  if (!task || typeof task !== 'object') return false;
  if (task.required === false || task.isRequired === false || task.notApplicable === true) return false;
  const approval = normalizedStatus(
    task.approvalStatus
    || task.approval_status
    || task.suggestionStatus
    || task.suggestion_status,
  );
  return !['rejected', 'declined', 'denied'].includes(approval);
};

const taskIsComplete = (task) => (
  task?.isCompleted === true
  || task?.completed === true
  || task?.is_completed === true
  || task?.done === true
  || Boolean(task?.completedAt || task?.completed_at)
  || ['completed', 'complete', 'done'].includes(normalizedStatus(task?.status || task?.state))
);

const requiredNotificationRecipients = (meta = {}) => {
  const source = Array.isArray(meta.recipientIds) && meta.recipientIds.length
    ? meta.recipientIds
    : (Array.isArray(meta.assignedUserIds) && meta.assignedUserIds.length
      ? meta.assignedUserIds
      : (Array.isArray(meta.assignees)
        ? meta.assignees.map((assignee) => assignee?.userId || assignee?.id)
        : []));
  return [...new Set(source.filter(Boolean).map(String))];
};

const getPlanCardCompletion = (item) => {
  const meta = item?.meta || {};

  if (item?.group === 'notification') {
    const requiredRecipients = requiredNotificationRecipients(meta);
    const openedRecipients = new Set(
      (Array.isArray(meta.openedRecipientIds) ? meta.openedRecipientIds : [])
        .filter(Boolean)
        .map(String),
    );
    const everyRequiredRecipientOpened = requiredRecipients.length > 0
      && requiredRecipients.every((userId) => openedRecipients.has(userId));
    const aggregateAcknowledgedAt = latestValidDate([
      meta.acknowledgedAt,
      meta.aggregateAcknowledgedAt,
      meta.allRecipientsOpenedAt,
      meta.completedAt,
    ]);
    return {
      completed: Boolean(
        aggregateAcknowledgedAt
        || everyRequiredRecipientOpened
        || (requiredRecipients.length === 0 && openedRecipients.size > 0)
      ),
      completedAt: aggregateAcknowledgedAt,
    };
  }

  if (PLAN_VERIFICATION_GROUPS.has(item?.group)) {
    const verificationStatus = normalizedStatus(meta.verificationStatus);
    const completed = ['confirmed', 'false alarm', 'false alert'].includes(verificationStatus);
    return {
      completed,
      completedAt: completed
        ? latestValidDate([meta.verifiedAt, meta.acknowledgedAt, meta.completedAt])
        : null,
    };
  }

  if (PLAN_ACTION_GROUPS.has(item?.group)) {
    const requiredTasks = (Array.isArray(meta.tasks) ? meta.tasks : []).filter(taskIsRequired);
    const allRequiredTasksCompleted = requiredTasks.length > 0 && requiredTasks.every(taskIsComplete);
    const performanceStatus = normalizedStatus(meta.performanceStatus);
    const explicitlyPerformed = (
      ['performed', 'completed', 'complete', 'done'].includes(performanceStatus)
      || Boolean(meta.performedAt || meta.completedAt || meta.allTasksCompletedAt)
    );
    const completed = explicitlyPerformed || allRequiredTasksCompleted;
    return {
      completed,
      // Be conservative when both a whole-card completion and task-level
      // completions exist: the latest authoritative timestamp is when the
      // assigned work was completely finished.
      completedAt: completed
        ? latestValidDate([
          meta.performedAt,
          meta.completedAt,
          meta.allTasksCompletedAt,
          ...(allRequiredTasksCompleted
            ? requiredTasks.flatMap((task) => [
              task.completedAt,
              task.completed_at,
              task.performedAt,
              task.performed_at,
            ])
            : []),
        ])
        : null,
    };
  }

  return { completed: false, completedAt: null };
};

const planCardIsActive = (item, incident) => {
  if (!item || item.is_active === false) return false;
  const meta = item.meta || {};
  if (meta.suppressed || meta.superseded || meta.isSuperseded || item.superseded) return false;
  if (meta.offDuty === true && meta.forced !== true) return false;

  const cardPlanState = normalizedStatus(item.plan_state);
  if (cardPlanState) return cardPlanState === 'active';
  const incidentPlanState = normalizedStatus(incident?.plan_state);
  if (incidentPlanState) return incidentPlanState === 'active';
  // Exercise timeline cards pre-date plan_state. Their Active incident stage is
  // the equivalent execution state and keeps this helper backwards compatible.
  if (incident?.is_exercise || incident?.is_scenario) {
    return normalizedStatus(incident?.status) === 'active';
  }
  return false;
};

const emptyPlanCompliance = (total = 0) => ({
  score: null,
  completionRate: null,
  slaRate: null,
  compliant: 0,
  completed: 0,
  pending: 0,
  onTime: 0,
  due: 0,
  overdue: 0,
  total,
  slaMet: 0,
  slaTracked: 0,
  slaAssessed: 0,
  slaLate: 0,
  slaPending: 0,
  late: 0,
  timingUnknown: 0,
  eligibleCards: 0,
  recipientObligations: 0,
  sharedCardObligations: 0,
});

// Plan Adherence = responder-completed, SLA-compliant response obligations /
// applicable plan obligations. Every active non-conditional obligation enters
// the denominator as soon as the plan is activated, including future T+ cards.
// Conditional obligations enter only after their runtime condition fires.
// Notification cards expand to one
// obligation per required recipient; verification and response-action cards
// remain shared obligations because their schema stores one shared outcome.
// Sending a card is only delivery and never earns adherence credit.
export const calculatePlanExecutionCompliance = (
  timeline = [],
  incident = {},
  nowValue = new Date(),
  _graceSeconds = 60,
) => {
  // The historical fourth argument represented dispatch grace. It is retained
  // for call compatibility but intentionally cannot relax responder SLAs.
  void _graceSeconds;
  const planItems = selectTimelinePreviewItems(timeline, incident);
  const items = planItems.filter((item) => (
    PLAN_EXECUTION_GROUPS.has(item?.group)
    && planCardIsActive(item, incident)
  ));
  const activation = toValidDate(
    incident?.plan_activated_at
    || incident?.activated_at
    || items.find((item) => item?.plan_activated_at)?.plan_activated_at,
  );
  const now = toValidDate(nowValue);
  if (!activation || !now) {
    return emptyPlanCompliance(items.length);
  }
  const effectiveOffsets = buildEffectivePlanOffsetMap(planItems);
  let due = 0;
  let compliant = 0;
  let completed = 0;
  let pending = 0;
  let slaMet = 0;
  let slaTracked = 0;
  let slaAssessed = 0;
  let slaLate = 0;
  let slaPending = 0;
  let late = 0;
  let overdue = 0;
  let timingUnknown = 0;
  let eligibleCards = 0;
  let recipientObligations = 0;
  let sharedCardObligations = 0;

  const addObligation = ({
    isCompleted,
    completedAt,
    startedAt,
    within,
    runtimeStarted = true,
    recipient = false,
  }) => {
    due += 1;
    if (recipient) recipientObligations += 1;
    else sharedCardObligations += 1;
    if (isCompleted) completed += 1;
    else pending += 1;

    if (within == null) {
      if (isCompleted) compliant += 1;
      return;
    }

    slaTracked += 1;
    if (!runtimeStarted) {
      slaPending += 1;
      return;
    }
    const deadlineAt = startedAt
      ? new Date(startedAt.getTime() + within * 60 * 1000)
      : null;
    if (isCompleted) {
      if (!completedAt || !deadlineAt) {
        // Completion without a real timestamp cannot prove an SLA result.
        timingUnknown += 1;
      } else if (completedAt.getTime() <= deadlineAt.getTime()) {
        slaMet += 1;
        slaAssessed += 1;
        compliant += 1;
      } else {
        late += 1;
        slaLate += 1;
        slaAssessed += 1;
      }
      return;
    }

    if (!deadlineAt) {
      timingUnknown += 1;
    } else if (now.getTime() > deadlineAt.getTime()) {
      overdue += 1;
      slaLate += 1;
      slaAssessed += 1;
    } else {
      slaPending += 1;
    }
  };

  items.forEach((item) => {
    const scheduledAt = new Date(
      activation.getTime() + Number(effectiveOffsets[item.id] || 0) * 60 * 1000,
    );
    const meta = item?.meta || {};
    const firedAt = toValidDate(meta.firedAt);
    const explicitlyFired = meta.fired === true || Boolean(
      firedAt && firedAt.getTime() <= now.getTime(),
    );
    const scheduledOffset = Number(meta.fireOffsetMinutes || 0);
    const actualDispatchAt = toValidDate(
      meta.firedAt
      || meta.push_sent_at
      || meta.pushSentAt
      || meta.dispatchedAt
      || meta.sentAt,
    );
    const notifiedAt = toValidDate(meta.notifiedAt);
    // Activation pre-stamps future T+ cards with their scheduled notifiedAt.
    // For immediate/manual cards it is the stronger responder-visible SLA
    // start and may legitimately follow the scheduler's firedAt timestamp.
    // Delayed cards still require a fired/delivery marker from the scheduler.
    const runtimeStartedAt = (
      (!Number.isFinite(scheduledOffset) || scheduledOffset <= 0)
        ? (notifiedAt || actualDispatchAt)
        : actualDispatchAt
    );
    const runtimeStarted = explicitlyFired || Boolean(
      runtimeStartedAt && runtimeStartedAt.getTime() <= now.getTime(),
    );
    const startedAt = runtimeStartedAt || scheduledAt;

    // A conditional card is not an obligation until its runtime condition
    // actually fires. Every normal card enters immediately when the plan is
    // activated, but remains pending until it really fires or is dispatched.
    if (meta.conditional && !explicitlyFired) return;
    eligibleCards += 1;

    const sla = meta.sla || null;
    const rawWithin = sla?.withinMinutes ?? meta.slaMinutes ?? meta.sla_minutes;
    const within = rawWithin != null && Number.isFinite(Number(rawWithin))
      ? Math.max(0, Number(rawWithin))
      : null;

    if (item.group === 'notification') {
      const recipientIds = requiredNotificationRecipients(meta);
      const openedRecipientIds = new Set(
        (Array.isArray(meta.openedRecipientIds) ? meta.openedRecipientIds : [])
          .filter(Boolean)
          .map(String),
      );
      const aggregateCompletion = getPlanCardCompletion(item);
      if (recipientIds.length > 0) {
        recipientIds.forEach((userId) => {
          addObligation({
            isCompleted: runtimeStarted && (
              openedRecipientIds.has(userId) || Boolean(aggregateCompletion.completedAt)
            ),
            // The schema currently persists only an aggregate acknowledgment
            // timestamp. A bare opened-recipient id proves completion but not
            // SLA timing, so it deliberately receives no fabricated SLA credit.
            completedAt: runtimeStarted ? aggregateCompletion.completedAt : null,
            startedAt,
            within,
            runtimeStarted,
            recipient: true,
          });
        });
      } else {
        addObligation({
          isCompleted: runtimeStarted && aggregateCompletion.completed,
          completedAt: runtimeStarted ? aggregateCompletion.completedAt : null,
          startedAt,
          within,
          runtimeStarted,
        });
      }
      return;
    }

    const completion = getPlanCardCompletion(item);
    addObligation({
      isCompleted: runtimeStarted && completion.completed,
      completedAt: runtimeStarted ? completion.completedAt : null,
      startedAt,
      within,
      runtimeStarted,
    });
  });
  const oneDecimalPercent = (numerator, denominator) => (
    denominator > 0 ? Math.round((numerator * 1000) / denominator) / 10 : null
  );
  return {
    score: oneDecimalPercent(compliant, due),
    completionRate: oneDecimalPercent(completed, due),
    slaRate: oneDecimalPercent(slaMet, slaAssessed),
    compliant,
    completed,
    pending,
    // Kept as a compatibility alias for callers that previously consumed the
    // dispatch-oriented field. It now means completed with adherence credit.
    onTime: compliant,
    due,
    overdue,
    total: items.length,
    slaMet,
    slaTracked,
    slaAssessed,
    slaLate,
    slaPending,
    late,
    timingUnknown,
    eligibleCards,
    recipientObligations,
    sharedCardObligations,
  };
};

// True when the inject has required roles that aren't fully staffed yet (Requested-stage planning).
const needsPersonnel = (it) => {
  const roles = it?.meta?.scenarioRoles;
  if (!Array.isArray(roles) || !roles.length) return false;
  const ra = it?.meta?.roleAssignments || {};
  return roles.some((r) => (ra[r?.role]?.length || 0) < Math.max(1, parseInt(r?.count, 10) || 1));
};

// People assigned to this inject's roles (set at the Requested stage), grouped by role so each
// chip reads "Role: name1, name2". Returns [{ role, names: [] }] for roles that have assignees.
export const isScenarioRecommendationAssignment = (assignment = {}) => {
  const metadata = assignment?.metadata || assignment?.meta || {};
  if (
    assignment?.autoAssignedFromScenario === true
    || assignment?.auto_assigned_from_scenario === true
    || metadata?.autoAssignedFromScenario === true
    || metadata?.auto_assigned_from_scenario === true
  ) return true;
  const source = String(
    assignment?.assignmentSource
    || assignment?.assignment_source
    || assignment?.autoAssignmentSource
    || assignment?.auto_assignment_source
    || metadata?.assignmentSource
    || metadata?.assignment_source
    || '',
  ).trim().toLowerCase().replace(/[\s-]+/g, '_');
  return source === 'scenario_recommendation' || source === 'scenario_default_candidate';
};

const getAssignedByRole = (it) => {
  const ra = it?.meta?.roleAssignments;
  if (!ra || typeof ra !== 'object') return [];
  const out = [];
  Object.entries(ra).forEach(([role, list]) => {
    const names = (Array.isArray(list) ? list : []).map((a) => a?.name).filter(Boolean);
    if (names.length) out.push({
      role,
      names,
      autoAssignedFromScenario: (Array.isArray(list) ? list : []).some(isScenarioRecommendationAssignment),
    });
  });
  return out;
};

export const AssignedRoleChip = ({ role, names = [], dark = true, testId }) => (
  <span
    className={`inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[9px] font-semibold ${dark ? 'border-violet-700 bg-violet-950/60 text-violet-300' : 'border-violet-200 bg-violet-100 text-violet-700'}`}
    data-testid={testId}
  >
    <Users className="h-2.5 w-2.5" /> {role}: {names.join(', ')}
  </span>
);

export const ExerciseTimelinePreview = ({ incident, refreshKey = 0, variant = 'light', compact = false, renderInsideItem = null, renderAfterItem = null, itemsOverride = null }) => {
  const dk = variant === 'dark';
  const [fetchedItems, setFetchedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [merged] = useState(false); // merge feature removed — always expanded
  const [expandedBuckets, setExpandedBuckets] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`notifBuckets-${incident?.id}`) || '{}'); } catch (e) { return {}; }
  });
  const hasItemsOverride = Array.isArray(itemsOverride);
  const items = useMemo(() => (
    hasItemsOverride
      ? selectTimelinePreviewItems(itemsOverride, incident)
      : fetchedItems
  ), [fetchedItems, hasItemsOverride, incident?.is_exercise, incident?.is_scenario, itemsOverride]);

  // Merge feature removed; nothing to sync here.
  useEffect(() => {}, [incident?.id]);

  // Stay in sync with per-bucket expansion toggled in the Injector Manager.
  useEffect(() => {
    const handler = (e) => {
      if (!e.detail || e.detail.incidentId !== incident?.id) return;
      setExpandedBuckets(e.detail.buckets || {});
    };
    window.addEventListener('notificationBucketsChanged', handler);
    return () => window.removeEventListener('notificationBucketsChanged', handler);
  }, [incident?.id]);

  const toggleBucket = (b) => {
    setExpandedBuckets((p) => {
      const next = { ...p, [b]: !p[b] };
      try { localStorage.setItem(`notifBuckets-${incident?.id}`, JSON.stringify(next)); } catch (e) {}
      window.dispatchEvent(new CustomEvent('notificationBucketsChanged', { detail: { incidentId: incident?.id, buckets: next } }));
      return next;
    });
  };

  const bucketOf = (it) => {
    const m = it?.meta || {};
    if ((m.crewIds && m.crewIds.length) || m.crewId || m.crew_id) return 'crew';
    if ((m.departmentIds && m.departmentIds.length) || m.department_id || m.departmentId || m.department) return 'department';
    return 'user';
  };

  // Effective fire time: a conditional card can never start before its source's condition is met,
  // so its displayed offset is max(its own offset, source-condition-met-offset) — computed recursively.
  const effectiveOffsetMap = useMemo(() => buildEffectivePlanOffsetMap(items), [items]);
  const effOffset = (it) => (effectiveOffsetMap[it?.id] ?? getOffset(it));

  const [roleFilter, setRoleFilter] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState({});
  const itemRoles = (it) => (it?.meta?.scenarioRoles || []).map((r) => r?.role).filter(Boolean);
  const allRoles = useMemo(() => {
    const s = new Set();
    items.forEach((it) => itemRoles(it).forEach((r) => s.add(r)));
    return Array.from(s);
  }, [items]);
  const conditionalCount = useMemo(() => items.filter((it) => it?.meta?.conditional).length, [items]);
  // Distinct roles + total personnel (per-role peak count, matching Required Roles).
  const personnelCount = useMemo(() => {
    const byRole = {};
    items.forEach((it) => (it?.meta?.scenarioRoles || []).forEach((r) => {
      const n = String(r?.role || '').trim();
      if (!n) return;
      byRole[n] = Math.max(byRole[n] || 0, Number(r?.count) || 1);
    }));
    return Object.values(byRole).reduce((s, c) => s + c, 0);
  }, [items]);

  // Open the matching card in the Injector Manager — expanding its (possibly collapsed) section
  // and any conditional containers above it — then scroll to it.
  const openCard = (it) => {
    const byId = new Map(items.map((x) => [x.id, x]));
    const sourceIds = [];
    let cur = it;
    let guard = 0;
    while (cur?.meta?.conditional && guard < 20) {
      sourceIds.push(cur.meta.conditional.sourceItemId);
      cur = byId.get(cur.meta.conditional.sourceItemId);
      guard += 1;
    }
    const groupKey = (cur || it).group;
    dispatchTimelineFocus({
      incidentId: incident?.id,
      itemId: it.id,
      groupKey,
      sourceIds,
      reason: 'plan-selection',
      pauseAfterFocus: true,
    });
  };

  // Conditional follow-ups are nested directly beneath their source card (their clock only starts
  // once the source's condition is met), and orphaned conditionals (source removed) are hidden.
  const displayItems = useMemo(() => {
    const all = items;
    if (roleFilter) {
      // Role view: a flat, time-ordered list of just this role's injects.
      return all
        .filter((it) => itemRoles(it).includes(roleFilter))
        .sort((a, b) => effOffset(a) - effOffset(b))
        .map((it) => ({ ...it, _depth: 0 }));
    }
    const childrenOf = (id) => all.filter((it) => it?.meta?.conditional?.sourceItemId === id);
    const roots = all
      .filter((it) => !it?.meta?.conditional)
      .sort((a, b) => effOffset(a) - effOffset(b));
    const out = [];
    const walk = (it, depth) => {
      out.push({ ...it, _depth: depth });
      childrenOf(it.id)
        .sort((a, b) => effOffset(a) - effOffset(b))
        .forEach((k) => walk(k, depth + 1));
    };
    roots.forEach((r) => walk(r, 0));
    return out;
  }, [items, effectiveOffsetMap, roleFilter]);

  const load = useCallback(async () => {
    if (!incident?.id) return;
    setLoading(true);
    try {
      const res = await CommanServices.getincidentTimeline(incident.id);
      const data = res?.data ?? res ?? [];
      const list = Array.isArray(data) ? data : (data.items || []);
      setFetchedItems(selectTimelinePreviewItems(list, incident));
    } catch (e) {
      setFetchedItems([]);
    } finally {
      setLoading(false);
    }
  }, [incident?.id, incident?.is_exercise, incident?.is_scenario]);

  useEffect(() => {
    if (hasItemsOverride) return;
    load();
  }, [hasItemsOverride, load, refreshKey]);

  // Auto-update whenever any inject card is added/edited/deleted elsewhere.
  useEffect(() => {
    if (hasItemsOverride) return undefined;
    const refreshTimeline = (event) => {
      const changedIncidentId = event?.detail?.incident_id;
      if (changedIncidentId && String(changedIncidentId) !== String(incident?.id)) return;
      load();
    };
    const refreshIncident = (event) => {
      const changedIncidentId = event?.detail?.incident_id;
      if (changedIncidentId && String(changedIncidentId) !== String(incident?.id)) return;
      load();
    };
    window.addEventListener('timelineRefresh', refreshTimeline);
    window.addEventListener('incidentRealtimeSync', refreshIncident);
    return () => {
      window.removeEventListener('timelineRefresh', refreshTimeline);
      window.removeEventListener('incidentRealtimeSync', refreshIncident);
    };
  }, [hasItemsOverride, incident?.id, load]);

  const isActive = String(incident?.status || '').toLowerCase() === 'active';

  return (
    <div className={compact ? 'bg-transparent p-0' : (dk ? 'bg-transparent p-1' : 'bg-white p-5')} data-testid="exercise-timeline-preview">
      {!compact && <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className={`w-4 h-4 ${dk ? 'text-indigo-400' : 'text-indigo-600'}`} />
          <h3 className={`text-[11px] font-black uppercase tracking-widest ${dk ? 'text-slate-300' : 'text-gray-700'}`}>{(!incident?.is_exercise && !incident?.is_scenario) ? 'Response Plan Timeline (Injection Sequence)' : 'Exercise Timeline (Injection Sequence)'}</h3>
        </div>
        <div className="flex items-center gap-1.5">
          {allRoles.length > 0 && (
            <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 inline-flex items-center gap-1 border ${dk ? 'text-emerald-300 bg-emerald-950/60 border-emerald-800' : 'text-emerald-700 bg-emerald-50 border-emerald-200'}`} data-testid="timeline-preview-roles-count">
              <Users className="w-3 h-3" /> {allRoles.length} {allRoles.length === 1 ? 'role' : 'roles'} · {personnelCount} personnel
            </span>
          )}
          {conditionalCount > 0 && (
            <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 inline-flex items-center gap-1 border ${dk ? 'text-amber-300 bg-amber-950/60 border-amber-800' : 'text-amber-700 bg-amber-50 border-amber-200'}`} data-testid="timeline-preview-conditional-count">
              <Zap className="w-3 h-3" /> {conditionalCount} conditional
            </span>
          )}
          <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 border ${dk ? 'text-indigo-300 bg-indigo-950/60 border-indigo-800' : 'text-indigo-600 bg-indigo-50 border-indigo-100'}`} data-testid="timeline-preview-count">
            {items.length} {incident?.is_exercise ? (items.length === 1 ? 'inject' : 'injects') : (items.length === 1 ? 'planned card' : 'planned cards')}
          </span>
        </div>
      </div>}

      {/* Role filter — view the sequence for a single role. Compact response-plan
          reviews keep the same scenario-style chips without the larger heading. */}
      {allRoles.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="timeline-preview-role-filter">
          <span className={`text-[10px] font-bold uppercase tracking-wide ${dk ? 'text-slate-500' : 'text-gray-400'}`}>Role:</span>
          <button
            type="button"
            onClick={() => setRoleFilter(null)}
            data-testid="role-filter-all"
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition-colors ${!roleFilter ? 'bg-indigo-600 text-white border-indigo-600' : (dk ? 'bg-slate-800 text-slate-300 border-slate-600 hover:border-indigo-500' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300')}`}
          >
            All
          </button>
          {allRoles.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRoleFilter(roleFilter === r ? null : r)}
              data-testid={`role-filter-${r}`}
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition-colors ${roleFilter === r ? 'bg-violet-600 text-white border-violet-600' : (dk ? 'bg-slate-800 text-violet-300 border-violet-800 hover:border-violet-500' : 'bg-white text-violet-700 border-violet-200 hover:border-violet-400')}`}
            >
              {r}
            </button>
          ))}
        </div>
      )}

      {!compact && !isActive && (
        <p className={`text-[11px] mb-3 ${dk ? 'text-slate-500' : 'text-gray-400'}`}>
          {incident?.is_exercise
            ? 'Offsets are measured from the moment the exercise is activated. Clocks start at activation.'
            : 'Offsets are measured from the moment the response plan is activated. Clocks and SLAs start once you activate the plan.'}
        </p>
      )}

      {items.length === 0 ? (
        <p className={`text-sm py-3 text-center ${dk ? 'text-slate-500' : 'text-gray-400'}`} data-testid="timeline-preview-empty">
          {loading ? 'Loading injects…' : 'No injects configured yet. Any card you add (except Report & Detection) appears here automatically.'}
        </p>
      ) : (
        <div className="relative pl-4">
          <div className={`absolute left-[7px] top-1 bottom-1 w-px bg-gradient-to-b to-transparent ${dk ? 'from-indigo-700 via-slate-700' : 'from-indigo-200 via-gray-200'}`} />
          <div className="space-y-3">
            {displayItems.map((it) => {
              if (it.merged) {
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => toggleBucket(it.bucket)}
                    className="relative w-full flex items-center gap-3 text-left"
                    data-testid={`timeline-preview-${it.id}`}
                  >
                    <span className={`absolute -left-4 mt-0 h-3 w-3 rounded-full ring-2 bg-amber-500 ${dk ? 'ring-slate-900' : 'ring-white'}`} />
                    <div className={`flex-1 rounded-lg px-3 py-2 transition-colors flex items-center gap-1.5 ${dk ? 'bg-amber-950/40 hover:bg-amber-950/60' : 'bg-amber-50 hover:bg-amber-100'}`}>
                      <Bell className={`w-3.5 h-3.5 ${dk ? 'text-amber-400' : 'text-amber-600'}`} />
                      <span className={`text-sm font-semibold ${dk ? 'text-slate-200' : 'text-gray-800'}`}>{it.count} {it.bucket} notification{it.count > 1 ? 's' : ''}</span>
                      <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${dk ? 'text-amber-400' : 'text-amber-600'} ${it.open ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                );
              }
              const meta = SECTION_META[it.group] || DEFAULT_META;
              const Icon = meta.icon;
              const offset = effOffset(it);
              const sla = getSla(it);
              const cond = it?.meta?.conditional;
              const itemTasks = it.group === 'response_action' ? (it?.meta?.tasks || []) : [];
              const tasksOpen = !!expandedTasks[it.id];
              const inlineResult = renderInsideItem?.(it) || null;
              const inlineContent = inlineResult && Object.prototype.hasOwnProperty.call(inlineResult, 'content')
                ? inlineResult.content
                : inlineResult;
              const inlineGap = Math.max(0, Number(inlineResult?.gap || 0));
              const hasInlineContent = !!inlineContent;
              const assignedByRole = getAssignedByRole(it);
              const hasScenarioAutoAssignment = assignedByRole.some((group) => group.autoAssignedFromScenario);
              return (
                <React.Fragment key={it.id}>
                <div
                  className="relative flex w-full items-start gap-2 text-left"
                  style={{ paddingLeft: it._depth ? `${it._depth * 22}px` : undefined }}
                >
                  {it._depth > 0 && (
                    <span className="absolute left-1 top-0 bottom-0 border-l-2 border-dashed border-amber-300" style={{ left: `${it._depth * 22 - 14}px` }} />
                  )}
                  {it._depth > 0 ? (
                    <span className="shrink-0 mt-1 text-amber-500 font-bold text-sm leading-none">↳</span>
                  ) : (
                    <span className={`absolute -left-4 mt-1 h-3 w-3 rounded-full ring-2 ${dk ? 'ring-slate-900' : 'ring-white'} ${meta.dot}`} />
                  )}
                  <span className={`shrink-0 inline-flex items-center justify-center min-w-[52px] h-6 px-2 rounded-md text-white text-[11px] font-bold ${cond ? 'bg-amber-600' : (dk ? 'bg-slate-600' : 'bg-gray-900')}`}>
                    T+{offset}m
                  </span>
                  <div className={`min-w-0 flex-1 overflow-hidden rounded-lg ${dk
                    ? (cond ? 'bg-slate-800/60 border border-dashed border-amber-700/60' : 'bg-slate-800/80 border border-slate-700')
                    : `${cond ? 'bg-gray-100 border border-dashed border-gray-300' : meta.bg}`}`}>
                    <div
                      id={`seq-${it.id}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => openCard(it)}
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        openCard(it);
                      }}
                      className={`px-3 py-2 transition ${dk ? 'hover:bg-slate-800' : 'hover:brightness-95'}`}
                      data-testid={`timeline-preview-item-${it.id}`}
                    >
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Icon className={`w-3.5 h-3.5 ${dk ? meta.color.replace('-600', '-400') : meta.color}`} />
                      <span className={`text-[10px] font-bold uppercase tracking-wide ${dk ? meta.color.replace('-600', '-400') : meta.color}`}>{meta.label}</span>
                      {cond && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-white bg-amber-500 rounded-full px-1.5 py-0.5" data-testid={`seq-conditional-badge-${it.id}`}>
                          <Zap className="w-2.5 h-2.5" /> CONDITIONAL
                        </span>
                      )}
                      {cond && cond.sourceTaskIndex != null && (
                        <span className={`text-[9px] font-bold rounded-full px-1.5 py-0.5 border ${dk ? 'text-amber-300 bg-amber-950/60 border-amber-700' : 'text-amber-700 bg-amber-100 border-amber-300'}`} data-testid={`seq-task-chip-${it.id}`}>
                          Task {cond.sourceTaskIndex + 1}
                        </span>
                      )}
                      {it?.meta?.addedDuringActive === true && (
                        <span className={`inline-flex items-center gap-0.5 text-[9px] font-bold rounded-full px-1.5 py-0.5 border ${dk ? 'text-emerald-300 bg-emerald-950/60 border-emerald-700' : 'text-emerald-700 bg-emerald-50 border-emerald-200'}`} data-testid={`seq-added-live-${it.id}`}>
                          Added live
                        </span>
                      )}
                      {sla !== undefined && (
                        <span className={`ml-auto text-[10px] font-semibold ${dk ? 'text-slate-400' : 'text-gray-500'}`}>SLA {sla}m</span>
                      )}
                      {inlineGap > 0 && (
                        <span className={`${sla === undefined ? 'ml-auto' : ''} inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${dk ? 'border-amber-700 bg-amber-950/60 text-amber-300' : 'border-amber-300 bg-amber-100 text-amber-700'}`} data-testid={`seq-staffing-gap-${it.id}`}>
                          <AlertTriangle className="h-2.5 w-2.5" /> {inlineGap} gap{inlineGap === 1 ? '' : 's'}
                        </span>
                      )}
                      {itemTasks.length > 0 && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); setExpandedTasks((p) => ({ ...p, [it.id]: !p[it.id] })); }}
                          data-testid={`seq-tasks-toggle-${it.id}`}
                          className={`${sla !== undefined || inlineGap > 0 ? '' : 'ml-auto'} inline-flex items-center gap-0.5 text-[9px] font-bold rounded-full px-1.5 py-0.5 cursor-pointer border ${dk ? 'text-indigo-300 bg-indigo-950/60 border-indigo-700 hover:bg-indigo-950' : 'text-indigo-600 bg-indigo-50 border-indigo-200 hover:bg-indigo-100'}`}
                        >
                          {itemTasks.length} tasks
                          <ChevronDown className={`w-3 h-3 transition-transform ${tasksOpen ? 'rotate-180' : ''}`} />
                        </span>
                      )}
                    </div>
                    <p className={`text-[11px] truncate mt-0.5 ${dk ? 'text-slate-400' : 'text-gray-500'}`}>→ {getAssignee(it)}</p>
                    {!hasInlineContent && (assignedByRole.length > 0 || hasScenarioAutoAssignment) && (
                      <div className="no-scrollbar mt-1 flex flex-nowrap items-center gap-1 overflow-x-auto whitespace-nowrap" data-testid={`seq-assigned-${it.id}`}>
                        {assignedByRole.map((g, gi) => (
                          <AssignedRoleChip key={gi} role={g.role} names={g.names} dark={dk} testId={`seq-assigned-role-${it.id}-${gi}`} />
                        ))}
                        {hasScenarioAutoAssignment && (
                          <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold ${dk ? 'border-emerald-800 bg-emerald-950/50 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`} data-testid={`seq-auto-assigned-${it.id}`}>
                            <ShieldCheck className="h-2.5 w-2.5" /> Auto-assigned
                          </span>
                        )}
                      </div>
                    )}
                    {!hasInlineContent && !incident?.is_scenario && needsPersonnel(it) && (
                      <div className="mt-1" data-testid={`seq-needs-personnel-${it.id}`}>
                        <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold border ${dk ? 'bg-amber-950/60 border-amber-700 text-amber-300' : 'bg-amber-100 border-amber-300 text-amber-700'}`}>
                          <AlertTriangle className="w-2.5 h-2.5" /> Needs personnel
                        </span>
                      </div>
                    )}
                    </div>
                    {hasInlineContent && (
                      <div className={`border-t ${dk ? 'border-slate-700/80' : 'border-gray-200/80'}`} data-testid={`timeline-inline-content-${it.id}`}>
                        {inlineContent}
                      </div>
                    )}
                  </div>
                </div>
                {renderAfterItem?.(it)}
                {itemTasks.length > 0 && tasksOpen && (
                  <div className="ml-[64px] mt-1 mb-1 space-y-1" data-testid={`seq-tasks-${it.id}`}>
                    {itemTasks.map((t, ti) => (
                      <div key={t.taskId || ti} className={`flex items-center gap-2 text-[11px] rounded-md px-2 py-1 border ${dk ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
                        <span className={`shrink-0 inline-flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-bold ${dk ? 'bg-slate-600 text-slate-200' : 'bg-gray-200 text-gray-600'}`}>{ti + 1}</span>
                        <span className={`flex-1 truncate ${dk ? 'text-slate-300' : 'text-gray-700'}`}>{t.taskText || t.task_text || `Task ${ti + 1}`}</span>
                        <span className={`shrink-0 text-[10px] font-semibold ${dk ? 'text-indigo-300' : 'text-indigo-600'}`}>SLA {parseInt(t.sla) || 0}m</span>
                      </div>
                    ))}
                  </div>
                )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseTimelinePreview;
